// Enhanced NotificationService.js - Fixed React Hooks Rules Violation

import { useState, useEffect, useCallback } from 'react';
import { hasUnreadMessages } from '../../Fetch/fetchVaadsMessages';

// Global state for real-time notifications across components
let globalNotificationState = {
  hasUnread: false,
  newMessageIds: new Set(),
  newAnswerIds: new Set(),
  listeners: new Set()
};

// Custom hook for managing vaads message notifications with real-time updates
export const useVaadsNotifications = () => {
  const [hasUnread, setHasUnread] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [newAnswerCount, setNewAnswerCount] = useState(0);

  // Function to update local state from global state
  const updateFromGlobalState = useCallback(() => {
    const totalNew = globalNotificationState.newMessageIds.size + globalNotificationState.newAnswerIds.size;
    const hasAnyUnread = globalNotificationState.hasUnread || totalNew > 0;
    
    setHasUnread(hasAnyUnread);
    setNewMessageCount(globalNotificationState.newMessageIds.size);
    setNewAnswerCount(globalNotificationState.newAnswerIds.size);
  }, []);

  // Function to check backend for unread messages (fallback)
  const checkUnreadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (userData.userId) {
        const result = await hasUnreadMessages(userData.userId);
        globalNotificationState.hasUnread = result.hasUnread || false;
        updateFromGlobalState();
      }
    } catch (error) {
      globalNotificationState.hasUnread = false;
      updateFromGlobalState();
    } finally {
      setLoading(false);
    }
  }, [updateFromGlobalState]);

  // Function to mark messages as read (clear notifications)
  const markAsRead = useCallback(() => {
    globalNotificationState.hasUnread = false;
    globalNotificationState.newMessageIds.clear();
    globalNotificationState.newAnswerIds.clear();
    updateFromGlobalState();
    
    // Notify all other components
    globalNotificationState.listeners.forEach(listener => listener());
  }, [updateFromGlobalState]);

  // Register this component as a listener for global state changes
  useEffect(() => {
    globalNotificationState.listeners.add(updateFromGlobalState);
    
    return () => {
      globalNotificationState.listeners.delete(updateFromGlobalState);
    };
  }, [updateFromGlobalState]);

  // Enhanced real-time event listeners
  useEffect(() => {
    const getCurrentUserId = () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        return userData.userId;
      } catch {
        return null;
      }
    };

    // Handle new message events
    const handleNewMessage = (event) => {
      const { message, userId } = event.detail;
      const currentUserId = getCurrentUserId();
      
      // Only notify if message is from another user
      if (userId && currentUserId && userId !== currentUserId) {
        globalNotificationState.newMessageIds.add(message.id);
        updateFromGlobalState();
      }
    };

    // Handle new answer events
    const handleNewAnswer = (event) => {
      const { messageId, answerUserId } = event.detail;
      const currentUserId = getCurrentUserId();
      
      // Only notify if answer is from another user
      if (answerUserId && currentUserId && answerUserId !== currentUserId) {
        globalNotificationState.newAnswerIds.add(messageId);
        updateFromGlobalState();
      }
    };

    // Handle messages marked as read
    const handleMessagesRead = (event) => {
      markAsRead();
    };

    // Handle generic refresh events
    const handleRefreshNotifications = async (event) => {
      const { reason } = event.detail || {};
      await checkUnreadMessages();
    };

    // Register all event listeners
    window.addEventListener('vaadsMessageCreated', handleNewMessage);
    window.addEventListener('vaadsMessageAnswered', handleNewAnswer);
    window.addEventListener('vaadsMessagesRead', handleMessagesRead);
    window.addEventListener('refreshNotifications', handleRefreshNotifications);

    // Initial load
    checkUnreadMessages();

    // Cleanup
    return () => {
      window.removeEventListener('vaadsMessageCreated', handleNewMessage);
      window.removeEventListener('vaadsMessageAnswered', handleNewAnswer);
      window.removeEventListener('vaadsMessagesRead', handleMessagesRead);
      window.removeEventListener('refreshNotifications', handleRefreshNotifications);
    };
  }, [checkUnreadMessages, updateFromGlobalState, markAsRead]);

  // Refresh notifications when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkUnreadMessages();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkUnreadMessages]);

  // Periodic refresh every 60 seconds (reduced frequency since we have real-time)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        checkUnreadMessages();
      }
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [checkUnreadMessages]);

  return {
    hasUnread,
    loading,
    newMessageCount,
    newAnswerCount,
    checkUnreadMessages,
    markAsRead
  };
};

// FIXED: Enhanced notification dot component with animation and count
export const NotificationDot = ({ 
  show, 
  count = 0, 
  showCount = false, 
  className = '',
  size = 'normal' // 'small', 'normal', 'large'
}) => {
  // FIXED: Move all hooks before any conditional logic
  useEffect(() => {
    const styleId = 'notification-animation-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes notificationPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.8;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // NOW apply conditional logic after all hooks
  if (!show) return null;

  const sizes = {
    small: { width: '8px', height: '8px', fontSize: '8px', top: '-4px', right: '-4px' },
    normal: { width: '12px', height: '12px', fontSize: '10px', top: '-6px', right: '-6px' },
    large: { width: '16px', height: '16px', fontSize: '11px', top: '-8px', right: '-8px' }
  };

  const sizeStyle = sizes[size] || sizes.normal;

  const dotStyle = {
    position: 'absolute',
    top: sizeStyle.top,
    right: sizeStyle.right,
    backgroundColor: '#dc3545',
    borderRadius: '50%',
    width: sizeStyle.width,
    height: sizeStyle.height,
    zIndex: 10,
    border: '2px solid white',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    animation: 'notificationPulse 2s ease-in-out infinite',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: sizeStyle.fontSize,
    fontWeight: 'bold',
    color: 'white',
    minWidth: showCount && count > 0 ? '16px' : sizeStyle.width,
    width: showCount && count > 0 ? 'auto' : sizeStyle.width,
    padding: showCount && count > 0 ? '0 4px' : '0'
  };

  return (
    <span 
      className={`notification-dot ${className}`}
      style={dotStyle}
      title={`${count > 0 ? count : ''} הודעות חדשות`}
    >
      {showCount && count > 0 && count < 100 ? count : (count >= 100 ? '99+' : '')}
    </span>
  );
};

// Enhanced badge component for sidebar/navigation
export const NotificationBadge = ({ 
  newMessageCount, 
  newAnswerCount, 
  className = '' 
}) => {
  const totalCount = newMessageCount + newAnswerCount;
  
  if (totalCount === 0) return null;

  return (
    <span 
      className={`notification-badge ${className}`}
      style={{
        backgroundColor: '#dc3545',
        color: 'white',
        borderRadius: '12px',
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: 'bold',
        minWidth: '18px',
        textAlign: 'center',
        marginRight: '8px',
        animation: 'notificationPulse 2s ease-in-out infinite'
      }}
      title={`${newMessageCount} הודעות חדשות, ${newAnswerCount} תגובות חדשות`}
    >
      {totalCount < 100 ? totalCount : '99+'}
    </span>
  );
};

export default {
  useVaadsNotifications,
  NotificationDot,
  NotificationBadge
};